import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ThemeProvider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import customTheme from '../../customTheme';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(0, 48, 83, 0.2)',
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #003053 0%, #385980 100%)',
    color: '#fff',
    padding: theme.spacing(2, 3),
    fontSize: '1.15rem',
    fontWeight: 600,
  },
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fc',
    minWidth: 320,
  },
  dialogContentText: {
    color: '#003053',
    fontSize: '0.95rem',
    lineHeight: 1.6,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#e8ecf2',
    borderTop: '1px solid #c5d0de',
  },
  cancelBtn: {
    color: '#003053',
    '&:hover': {
      backgroundColor: 'rgba(0, 48, 83, 0.08)',
    },
  },
  confirmBtn: {
    backgroundColor: '#dcc67d',
    color: '#003053',
    border: '1px solid #d1b455',
    '&:hover': {
      backgroundColor: '#d1b455',
      borderColor: '#c4a843',
    },
  },
}));

/**
 * Generic confirmation dialog with themed UI.
 * @param {string} title - Dialog title
 * @param {string} description - Dialog body text
 * @param {boolean} open - Whether the dialog is visible
 * @param {function} onCancel - Called when user cancels (Cancel button or backdrop click)
 * @param {function} onSuccess - Called when user confirms (Confirm button)
 * @param {string} [cancelLabel='Cancel'] - Cancel button label
 * @param {string} [confirmLabel='Confirm'] - Confirm button label
 */
const ConfirmDialog = ({
  title,
  description,
  open,
  onCancel,
  onSuccess,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
}) => {
  const classes = useStyles();

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onCancel}
        aria-labelledby="confirm-dialog-title"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle id="confirm-dialog-title" className={classes.dialogTitle}>
          {title}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText className={classes.dialogContentText}>
            {description}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={onCancel} className={classes.cancelBtn}>
            {cancelLabel}
          </Button>
          <Button onClick={onSuccess} className={classes.confirmBtn} variant="contained" autoFocus>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

ConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
};

export default ConfirmDialog;
